import React from 'react';
import { motion } from 'framer-motion';

interface PrivacyPolicyProps {
  onBackToHome?: () => void;
}

const PrivacyPolicy: React.FC<PrivacyPolicyProps> = ({ onBackToHome }) => {
  React.useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-[#F8F6F0] pt-20">
      <div className="container mx-auto px-6 py-16">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="max-w-4xl mx-auto"
        >
          <div className="mb-8">
            <motion.button
              onClick={onBackToHome}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="text-[#3783FE] hover:text-[#F22D5E] transition-colors duration-300 text-lg font-semibold mb-6"
            >
              ← Back to Home
            </motion.button>
            <h1 className="text-5xl md:text-6xl font-bold text-[#333333]">
              Privacy Policy
            </h1>
          </div>
          
          <div className="text-[#333333] space-y-6 text-lg leading-relaxed">
            <p className="text-xl font-semibold text-[#3783FE]">
              Effective Date: April 15, 2025
            </p>
            
            <p>
              Koral Convergence ("we", "our", or "us") is committed to protecting your privacy. 
              This Privacy Policy outlines how we handle your information when you visit our 
              website at koralconvergence.com.
            </p>

            <div className="space-y-6">
              <div>
                <h2 className="text-2xl font-bold text-[#333333] mb-3">1. Information We Collect</h2>
                <p className="mb-3">
                  Currently, we do not actively collect personal data such as names or emails 
                  through this website. However, we may automatically collect basic technical 
                  data such as:
                </p>
                <ul className="list-disc list-inside space-y-1 ml-4 mb-3">
                  <li>IP address</li>
                  <li>Browser type</li>
                  <li>Pages visited</li>
                  <li>Referral source</li>
                </ul>
                <p>
                  This is standard, anonymized data used to improve site performance.
                </p>
              </div>

              <div>
                <h2 className="text-2xl font-bold text-[#333333] mb-3">2. How We Use Your Information</h2>
                <p className="mb-3">Any data collected is used for:</p>
                <ul className="list-disc list-inside space-y-1 ml-4 mb-3">
                  <li>Understanding how users interact with our website</li>
                  <li>Improving user experience and site functionality</li>
                  <li>Monitoring traffic and analytics</li>
                </ul>
                <p>
                  We do not sell, rent, or share your data with third parties.
                </p>
              </div>

              <div>
                <h2 className="text-2xl font-bold text-[#333333] mb-3">3. Cookies</h2>
                <p>
                  We may use cookies or similar technologies to enhance site functionality and 
                  analyze traffic. You can control cookies via your browser settings.
                </p>
              </div>

              <div>
                <h2 className="text-2xl font-bold text-[#333333] mb-3">4. Third-Party Services</h2>
                <p className="mb-3">We may use third-party tools such as:</p>
                <ul className="list-disc list-inside space-y-1 ml-4 mb-3">
                  <li>Google Analytics</li>
                  <li>Netlify (hosting provider)</li>
                </ul>
                <p>
                  These providers may collect data as outlined in their own privacy policies.
                </p>
              </div>

              <div>
                <h2 className="text-2xl font-bold text-[#333333] mb-3">5. Links to Other Sites</h2>
                <p>
                  Our website may link to other websites (e.g. app stores, third-party tools). 
                  We are not responsible for the privacy practices or content of these external sites.
                </p>
              </div>

              <div>
                <h2 className="text-2xl font-bold text-[#333333] mb-3">6. Changes to This Policy</h2>
                <p>
                  We may update this policy from time to time. All changes will be posted on this 
                  page with the revised effective date.
                </p>
              </div>

              <div>
                <h2 className="text-2xl font-bold text-[#333333] mb-3">7. Contact Us</h2>
                <p className="mb-3">
                  If you have any questions or concerns about this Privacy Policy, contact us at:
                </p>
                <p className="text-[#3783FE] font-semibold text-xl">
                  team@koralconvergence.com
                </p>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default PrivacyPolicy;
